import React, { useState, useRef, useEffect } from "react";
import {
  IconChevronLeft,
  IconChevronRight,
  IconCalendar,
} from "@tabler/icons-react";
import { useLanguage } from "../context/LanguageContext";

interface YearPickerProps {
  value: string;
  onChange: (year: string) => void;
  error?: any;
  label?: string;
  required?: boolean;
  className?: string;
  labelClass?: string;
}

const YearPicker: React.FC<YearPickerProps> = ({
  value,
  onChange,
  error,
  label = "Year",
  required = false,
  className = "",
  labelClass = "",
}) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [decadeStart, setDecadeStart] = useState(() => {
    const currentYear = new Date().getFullYear();
    return currentYear - (currentYear % 12);
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update decadeStart if the selected value is parsed and outside the current decade range
  useEffect(() => {
    if (value) {
      const yearNum = parseInt(value, 10);
      if (
        !isNaN(yearNum) &&
        (yearNum < decadeStart || yearNum > decadeStart + 11)
      ) {
        setDecadeStart(yearNum - (yearNum % 12));
      }
    }
  }, [value, decadeStart]);

  const years = Array.from({ length: 12 }, (_, i) => decadeStart + i);

  return (
    <div ref={containerRef} className={`space-y-1 relative ${className}`}>
      {label && (
        <label
          className={`text-[#1E293B] text-xs ${labelClass} whitespace-nowrap`}
        >
          {t(label)}
          {required && <span className="text-red-600 ml-1">*</span>}
        </label>
      )}

      <div className="relative w-full">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full flex items-center justify-between rounded-xl py-[10px] px-4 outline-none text-xs bg-white border ${
            error ? "border-red-500" : "border-gray-200"
          } text-[#1E1E1E] text-left hover:border-gray-400 transition-colors`}
        >
          <span>{value || t("Select Year")}</span>
          <IconCalendar size={16} className="text-gray-400" />
        </button>

        {isOpen && (
          <div className="absolute left-0 bottom-full mb-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-2.5 min-w-[200px]">
            {/* Header */}
            <div className="flex items-center justify-between mb-2 px-1">
              <button
                type="button"
                onClick={() => setDecadeStart((prev) => prev - 12)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <IconChevronLeft size={16} className="text-gray-600" />
              </button>
              <span className="text-xs font-bold text-gray-700">
                {decadeStart} - {decadeStart + 11}
              </span>
              <button
                type="button"
                onClick={() => setDecadeStart((prev) => prev + 12)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <IconChevronRight size={16} className="text-gray-600" />
              </button>
            </div>

            {/* Grid of Years */}
            <div className="grid grid-cols-3 gap-1.5">
              {years.map((yr) => {
                const isSelected = String(yr) === value;
                return (
                  <button
                    key={yr}
                    type="button"
                    onClick={() => {
                      onChange(String(yr));
                      setIsOpen(false);
                    }}
                    className={`py-1.5 text-xs rounded-lg text-center font-medium transition-all ${
                      isSelected
                        ? "bg-[#FBD037] text-[#212121] shadow-sm font-semibold"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    {yr}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {error && (
        <span className="text-red-500 text-xs min-h-[0.5rem] block mt-0.5">
          {t(error.message || "Year is required")}
        </span>
      )}
    </div>
  );
};

export default YearPicker;
